import React, { useCallback, useMemo, useState } from "react";
import { GenderExpressionFE } from "../../../../constants/types/acf";
import { useAssessmentState } from "../../../../services/assessment-state";
import { AssessmentState } from "../../../../services/assessment-state/types";
import { useAssessmentHelperStoreActions } from "../../../../services/assessment-state/utils";
import { YesNo } from "../YesNo";
import { FadeSwitchAnimationWrapper } from "../shared";
import { TextButtonGroup, TextButton } from "../shared/TextButton";
import { AssessmentCopy } from "../utils";

const expressions = [
    {
        id: GenderExpressionFE.Male,
        value: "Male",
        carerValue: "Male",
    },
    {
        id: GenderExpressionFE.Female,
        value: "Female",
        carerValue: "Female",
    },
    {
        id: GenderExpressionFE.NonBinaryOrGenderFluid,
        value: "Non-binary",
        carerValue: "Non-binary",
    },
    {
        id: GenderExpressionFE.DifferentIdentity,
        value: "I identify differently",
        carerValue: "They identify differently",
    },
];

export const GenderIdentity: React.FC = () => {
    const { state, setState, isForSelf, getNextScreenIndex, scrollToTop } = useAssessmentState();

    const [showPartTwo, setShowPartTwo] = useState(false);
    const showingPartOne = !showPartTwo;

    const {
        setGoNextInterrupt,
        clearGoNextInterrupt,
        setGoPrevInterrupt,
        clearGoPrevInterrupt,
    } = useAssessmentHelperStoreActions();

    React.useEffect(() => {
        if (showingPartOne) {
            setGoNextInterrupt(() => {
                scrollToTop();
                setShowPartTwo(true);
            });
            clearGoPrevInterrupt();
        } else {
            clearGoNextInterrupt();
            setGoPrevInterrupt(() => {
                scrollToTop();
                setShowPartTwo(false);
            });
            clearGoPrevInterrupt();
        }
    }, [
        clearGoNextInterrupt,
        clearGoPrevInterrupt,
        scrollToTop,
        setGoNextInterrupt,
        setGoPrevInterrupt,
        showingPartOne,
    ]);

    const renderPartOne = useCallback(
        () => (
            <TextButtonGroup
                title={
                    isForSelf
                        ? AssessmentCopy.GENDER_QUESTION.SELF
                        : AssessmentCopy.GENDER_QUESTION.CARER
                }
            >
                {expressions.map(gender => (
                    <TextButton
                        key={gender.id}
                        isActive={state.gender_identity === gender.id}
                        onClick={() => {
                            setState({
                                gender_identity: gender.id,
                            });
                            scrollToTop();
                            setShowPartTwo(true);
                        }}
                    >
                        {isForSelf ? gender.value : gender.carerValue }
                    </TextButton>
                ))}
            </TextButtonGroup>
        ),
        [isForSelf, scrollToTop, setState, state.gender_identity],
    );

    const partTwoQuestionText = useMemo(() => {
        switch (state.gender_identity) {
            case GenderExpressionFE.Female:
                return "Are you looking for services that have specific supports for women or females?";
            case GenderExpressionFE.Male:
                return "Are you looking for services that have specific supports for men or males?";
            case GenderExpressionFE.NonBinaryOrGenderFluid:
            case GenderExpressionFE.DifferentIdentity:
                return "Are you looking for services that have specific supports for gender diverse people?";
            default:
                return "Are you looking for services that have specific supports for the chosen gender?";
        }
    }, [state.gender_identity]);

    const renderPartTwo = useCallback(
        () => (
            <YesNo
                question={partTwoQuestionText}
                onNo={() => {
                    setState({
                        specific_to_gender: false,
                        currentScreen: getNextScreenIndex(),
                    });
                }}
                onYes={() => {
                    setState({
                        specific_to_gender: true,
                        currentScreen: getNextScreenIndex(),
                    });
                }}
                value={state.specific_to_gender}
            />
        ),
        [getNextScreenIndex, partTwoQuestionText, setState, state.specific_to_gender],
    );

    return (
        <FadeSwitchAnimationWrapper changeKey={String(showPartTwo)}>
            {showPartTwo ? renderPartTwo() : renderPartOne()}
        </FadeSwitchAnimationWrapper>
    );
};

export const createMeta = (state: AssessmentState) => ({
    title: state.target === "MYSELF" ? "Your gender" : "Their gender",
    canProgress: state.gender_identity !== undefined,
    canSkip: true,
});
